import { SearchBreadcrumbText } from './loadMoreItemsForSelectedProvider.locstring.json';
import loc from 'owa-localize';
import clearFileProviderItems from './clearFileProviderItems';
import hideFilePickerErrorMessage from './errorMessageActions/hideFilePickerErrorMessage';
import showFilePickerErrorMessage from './errorMessageActions/showFilePickerErrorMessage';
import getFileProviderItemsRequestCount from './helpers/getFileProviderItemsRequestCount';
import pushFilePickerBreadcrumb from './pushFilePickerBreadcrumb';
import updateFileProviderItems from './updateFileProviderItems';
import type FilePickerPanelViewState from '../store/schema/FilePickerPanelViewState';
import type {
    default as FilePickerViewState,
    FilePickerBreadcrumb,
} from '../store/schema/FilePickerViewState';
import FilePickerErrorMessageType from '../types/FilePickerErrorMessageType';
import type GetFileProviderItemsResponse from '../types/GetFileProviderItemsResponse';
import createFileProviderItemsViewState from '../utils/createFileProviderItemsViewState';
import { ScopeBreadcrumbId, SearchBreadcrumbId } from '../utils/filePickerConstants';
import getOrSearchFileProviderItems from '../utils/getOrSearchFileProviderItems/getOrSearchFileProviderItems';
import { DatapointVariant, logUsage } from 'owa-analytics';
import { lazyRemoveAndAddFileProvider } from 'owa-fileprovider-store';
import { ADDABLE_FILE_PROVIDERS_LIST } from 'owa-fileprovider-store/lib/utils/constants';
import type AttachmentItemsPagingMetadata from 'owa-service/lib/contract/AttachmentItemsPagingMetadata';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';

import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import type {
    default as FileProviderItemViewState,
    FolderItemViewState,
} from '../store/schema/FileProviderItemViewState';

export const DefaultBreadcrumb: FilePickerBreadcrumb = {
    id: ScopeBreadcrumbId,
    name: 'Files',
    associatedItem: null,
};

function createBreadcrumbForFolder(folder: FolderItemViewState) {
    const { key, name } = folder;
    return <FilePickerBreadcrumb>{
        id: key,
        name: name,
        associatedItem: folder,
    };
}

function createBreadcrumbForSearch() {
    return <FilePickerBreadcrumb>{
        id: SearchBreadcrumbId,
        name: loc(SearchBreadcrumbText),
        associatedItem: null,
    };
}

function createBreadCrumbFromScope(viewState: FilePickerPanelViewState) {
    if (viewState.selectedProviderScope != null) {
        return viewState.selectedProviderScope.DisplayName !== ''
            ? <FilePickerBreadcrumb>{
                  id: ScopeBreadcrumbId,
                  name: viewState.selectedProviderScope.DisplayName,
                  associatedItem: null,
              }
            : null;
    }

    // Default breadcrumb is only used for testing purposes. We should always have a selected provioder scope.
    trace.warn('Filepicker - should always have a selected scope');
    return DefaultBreadcrumb;
}

const updateIsLoadingItems = action('updateIsLoadingItems')(function updateIsLoadingItems(
    isLoadingItems: boolean,
    viewState: FilePickerViewState
) {
    viewState.isLoadingItems = isLoadingItems;
});

const handleGetFileProviderItemsResponse = action('handleGetFileProviderItemsResponse')(
    function handleGetFileProviderItemsResponse(
        items: FileProviderItemViewState[],
        totalItemsCount: number,
        pagingMetadata: AttachmentItemsPagingMetadata,
        viewState: FilePickerPanelViewState,
        requestId: number
    ) {
        hideFilePickerErrorMessage(viewState.filePickerErrorMessageViewState);
        updateIsLoadingItems(false, viewState.filePickerViewState);

        // If a service gives us an incorrect totalItemsCount we can get stuck in a loop where we request indefinitely,
        // sending a request as soon as we get a response, and the responses will all be empty.
        // This was discovered due to a bug where ODC was counting a specific type of item in total items count but not
        // sending it back to the client. Ideally this code would never be hit, and any fixes would happen on the server, but it is
        // better to keep our code resilient from unexpected responses.
        if (
            viewState.filePickerViewState.totalItemsCount === totalItemsCount &&
            viewState.filePickerViewState.fileProviderItems.length > 0 &&
            items.length === 0
        ) {
            logUsage(
                'FilePickerEmptyResultExpectingMoreItems',
                {},
                { variant: DatapointVariant.WarmOnly }
            );
            viewState.filePickerViewState.totalItemsCount =
                viewState.filePickerViewState.fileProviderItems.length;
        } else {
            viewState.filePickerViewState.totalItemsCount = totalItemsCount;
        }

        viewState.filePickerViewState.pagingMetadata = pagingMetadata;
        updateFileProviderItems(items, viewState, requestId);
    }
);

export default action('loadMoreItemsForSelectedProvider')(
    /* folder should be null if root folder needs to be loaded */
    async function loadMoreItemsForSelectedProvider(
        viewState: FilePickerPanelViewState,
        isRefreshing: boolean = false,
        isPrefetch: boolean = false
    ) {
        if (!viewState.selectedFileProvider) {
            trace.warn(
                'Not allowed to get file provider items if there is no selected file provider'
            );
            return;
        }

        const {
            selectedFileProvider,
            filePickerViewState: {
                selectedFolder: folder,
                searchViewState: { isInSearchMode: isSearch },
            },
        } = viewState;

        if (isSearch) {
            showFilePickerErrorMessage(
                FilePickerErrorMessageType.Searching,
                viewState.filePickerErrorMessageViewState
            );
        } else {
            hideFilePickerErrorMessage(viewState.filePickerErrorMessageViewState);
        }

        const fileProviderType = selectedFileProvider.type;

        // Set the flag for loading items
        updateIsLoadingItems(true, viewState.filePickerViewState);

        let breadcrumbToPush;
        if (isSearch) {
            breadcrumbToPush = createBreadcrumbForSearch();
        } else if (folder) {
            breadcrumbToPush = createBreadcrumbForFolder(folder);
        } else {
            breadcrumbToPush = createBreadCrumbFromScope(viewState);
        }
        if (breadcrumbToPush) {
            pushFilePickerBreadcrumb(breadcrumbToPush, viewState.filePickerViewState);
        }

        const requestId: number = getFileProviderItemsRequestCount();

        try {
            const response: GetFileProviderItemsResponse = await getOrSearchFileProviderItems(
                viewState,
                isRefreshing,
                isPrefetch
            );

            const items = createFileProviderItemsViewState(response.items);

            if (isRefreshing) {
                // If we have no new items, leave them as is (return). Otherwise clear and then load the new items
                if (areSameItems(items, viewState.filePickerViewState.fileProviderItems)) {
                    updateIsLoadingItems(false, viewState.filePickerViewState);
                    return;
                } else {
                    clearFileProviderItems(viewState.filePickerViewState);
                }
            }

            handleGetFileProviderItemsResponse(
                items,
                response.totalItemsCount,
                response.pagingMetadata,
                viewState,
                requestId
            );
        } catch (error) {
            // We first check to make sure we haven't sent another request before getting this response.
            const lastRequestId = getFileProviderItemsRequestCount();
            if (requestId !== lastRequestId) {
                return;
            }
            updateIsLoadingItems(false, viewState.filePickerViewState);

            // If we are refreshing and have cached items, do not display error message
            if (!isRefreshing || viewState.filePickerViewState.totalItemsCount === 0) {
                // For now there are only two AttachmentResultCodes that lead to non-generic error messages.
                // If this number grows we should create a map between AttachmentResultCodes and ErrorMessageTypes instead of using if/else.
                let errorMessage = FilePickerErrorMessageType.Generic;
                if (error === AttachmentResultCode.AccessDenied) {
                    errorMessage = FilePickerErrorMessageType.AccessDenied;
                    if (ADDABLE_FILE_PROVIDERS_LIST.indexOf(fileProviderType) > -1) {
                        (await lazyRemoveAndAddFileProvider.import())(fileProviderType);
                    }
                } else if (error === AttachmentResultCode.NotFound) {
                    errorMessage = FilePickerErrorMessageType.NotFound;
                }

                showFilePickerErrorMessage(errorMessage, viewState.filePickerErrorMessageViewState);
            }
        }
    }
);

function areSameItems(
    newItems: FileProviderItemViewState[],
    oldItems: FileProviderItemViewState[]
): boolean {
    if (newItems.length !== oldItems.length) {
        return false;
    }
    for (let i = 0; i < newItems.length; i++) {
        // We check the name instead of the full item, since the old items have been added to
        // a satchel store, and made observable, but newItems haven't, so they fail equality checks
        if (newItems[i].name !== oldItems[i].name) {
            return false;
        }
    }

    return true;
}
